'use client';

import { produce } from 'immer';
import { createStore } from 'zustand';

import { ComponentProps, ElementType } from 'react';

export interface DialogObject<C extends ElementType = ElementType> {
	open: boolean;
	modal: boolean;
	Component: C | null;
	title?: string;
	// A render slot, not copy: callers pass <CopyText> for registry copy with emphasis.
	description?: string | React.ReactNode;
	props: React.ComponentProps<C> | null;
	onDialogClose: () => void;
	/** Classes for the Modal shell, e.g. the amber stage-change limit dialog. */
	contentClassName?: string;
}

export interface BaseDialogStoreState<C extends ElementType = ElementType> {
	dialogs: DialogObject<C>[];
	getCurrentDialogContent: () => {
		Component: BaseDialogStoreState['dialogs'][number]['Component'];
		props: BaseDialogStoreState['dialogs'][number]['props'];
	};
	setDialogOpen: (open: boolean, dialogIndex?: number) => void;
	setDialogModal: (modal: boolean, dialogIndex?: number) => void;
	clearDialogContent: (clearAll?: boolean) => void;
	setDialogContent: <T extends ElementType>(
		Component: T,
		props: React.ComponentProps<T>,
		options?: {
			title?: string;
			description?: string | React.ReactNode;
			onDialogClose?: () => void;
			append?: boolean;
			contentClassName?: string;
		}
	) => void;
}

export default function createDialogContentStore<
	T extends BaseDialogStoreState = BaseDialogStoreState,
>() {
	return createStore<BaseDialogStoreState>((set, get) => ({
		open: false,
		modal: true,
		dialogs: [],
		onDialogClose: () => {},
		getCurrentDialogContent: (): {
			Component: BaseDialogStoreState['dialogs'][number]['Component'];
			props: BaseDialogStoreState['dialogs'][number]['props'];
		} => {
			const dialogs = get()?.dialogs;
			if (dialogs?.length === 0)
				return {
					Component: null,
					props: null,
				};

			return {
				Component: dialogs[dialogs.length - 1]?.Component,
				props: dialogs[dialogs.length - 1]?.props,
			};
		},
		setDialogOpen: (open: boolean, dialogIndex?: number): void => {
			set(
				produce((state) => {
					const dialogs = [...(get()?.dialogs || [])];
					if (!dialogIndex && dialogIndex !== 0) dialogIndex = dialogs.length - 1;
					if (!dialogs[dialogIndex]) return;

					(state as T).dialogs[dialogIndex].open = open;
				})
			);
		},
		setDialogModal: (modal: boolean, dialogIndex?: number): void => {
			set(
				produce((state) => {
					const dialogs = get()?.dialogs;
					if (!dialogIndex && dialogIndex !== 0) dialogIndex = dialogs.length - 1;
					if (!dialogs[dialogIndex]) return;

					(state as T).dialogs[dialogIndex].modal = modal;
				})
			);
		},
		clearDialogContent: (clearAll: boolean = false): void => {
			set(
				produce((state) => {
					const dialogs = [...(get()?.dialogs || [])];
					if (dialogs?.length === 0) return;

					if (clearAll) {
						(state as T).dialogs = [];
					} else {
						dialogs.pop();
						(state as T).dialogs = dialogs;
					}
				})
			);
		},
		setDialogContent: <C extends ElementType>(
			Component: C,
			props: ComponentProps<C>,
			options?: {
				title?: string;
				description?: string | React.ReactNode;
				onDialogClose?: () => void;
				append?: boolean;
				contentClassName?: string;
			}
		): void => {
			const { title, description, onDialogClose, append = true, contentClassName } = options || {};

			set(
				produce((state) => {
					let newDialogs = [...(get()?.dialogs || [])];

					if (append) {
						newDialogs.push({
							Component,
							props,
							open: true,
							modal: true,
							title,
							description,
							onDialogClose: onDialogClose || (() => {}),
							contentClassName,
						});
					} else {
						newDialogs = [
							{
								Component,
								props,
								open: true,
								modal: true,
								title,
								description,
								onDialogClose: onDialogClose || (() => {}),
								contentClassName,
							},
						];
					}

					(state as T).dialogs = newDialogs;
				})
			);
		},
	}));
}
